<script>
/* global axios */
// Use Chatwoot's global, authenticated axios (carries the rotating
// devise-token-auth headers) -- NOT `import axios from 'axios'`, which is an
// unauthenticated copy and 401s. Mirrors dashboard/api/ApiClient.js.
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required } from '@vuelidate/validators';
import router from 'dashboard/routes/index';
import NextButton from 'dashboard/components-next/button/Button.vue';

import { isPhoneE164OrEmpty } from 'shared/helpers/Validators';

export default {
  components: {
    NextButton,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      inboxName: '',
      phoneNumber: '',
      channelId: '',
      token: '',
      isTesting: false,
      testState: null, // 'success' | 'warning' | 'error'
      testMessage: '',
    };
  },
  validations: {
    inboxName: { required },
    phoneNumber: { required, isPhoneE164OrEmpty },
    channelId: { required },
    token: { required },
  },
  computed: {
    ...mapGetters({ uiFlags: 'inboxes/getUIFlags' }),
    accountId() {
      return this.$route.params.accountId;
    },
    hasChannelId() {
      return Boolean(this.normalizedChannelId);
    },
    // Accept either the bare instance ID or a pasted 1msg "API url"
    // (https://api.1msg.io/BRA116431756/) -> bare ID.
    normalizedChannelId() {
      return this.channelId
        .trim()
        .replace(/^https?:\/\//i, '')
        .replace(/^api\.1msg\.io\/?/i, '')
        .replace(/\//g, '');
    },
    // Live preview of the inbound webhook URL the admin pastes into 1msg.io.
    webhookUrl() {
      const id = this.normalizedChannelId || '<your-channel-id>';
      return `${window.location.origin}/webhooks/onemsg/${id}`;
    },
    testClass() {
      return {
        'text-n-teal-11': this.testState === 'success',
        'text-n-amber-11': this.testState === 'warning',
        'text-n-ruby-11': this.testState === 'error',
      };
    },
  },
  methods: {
    copyWebhook() {
      if (!this.hasChannelId || !navigator.clipboard) return;
      navigator.clipboard.writeText(this.webhookUrl).then(() => {
        useAlert(this.$t('INBOX_MGMT.ADD.WHATSAPP.ONEMSG.WEBHOOK_URL.COPIED'));
      });
    },
    async testConnection() {
      if (!this.channelId.trim() || !this.token.trim()) {
        useAlert(this.$t('INBOX_MGMT.ADD.WHATSAPP.ONEMSG.TEST_MISSING'));
        return;
      }
      this.isTesting = true;
      this.testState = null;
      this.testMessage = '';
      try {
        const { data } = await axios.post(
          `/api/v1/accounts/${this.accountId}/smarticks/onemsg/test`,
          { channel_id: this.channelId.trim(), token: this.token.trim() }
        );
        if (data.status !== 'ok') {
          this.testState = 'error';
          this.testMessage = this.$t(
            'INBOX_MGMT.ADD.WHATSAPP.ONEMSG.TEST_CREDS_FAIL'
          );
        } else if (data.webhook === 'matched') {
          this.testState = 'success';
          this.testMessage = this.$t(
            'INBOX_MGMT.ADD.WHATSAPP.ONEMSG.TEST_WEBHOOK_MATCHED'
          );
        } else if (data.webhook === 'mismatch') {
          this.testState = 'warning';
          this.testMessage = this.$t(
            'INBOX_MGMT.ADD.WHATSAPP.ONEMSG.TEST_WEBHOOK_MISMATCH'
          );
        } else {
          this.testState = 'warning';
          this.testMessage = this.$t(
            'INBOX_MGMT.ADD.WHATSAPP.ONEMSG.TEST_WEBHOOK_MISSING'
          );
        }
      } catch (error) {
        this.testState = 'error';
        this.testMessage = this.$t(
          'INBOX_MGMT.ADD.WHATSAPP.ONEMSG.TEST_CREDS_FAIL'
        );
      } finally {
        this.isTesting = false;
        if (this.testMessage) useAlert(this.testMessage);
      }
    },
    async webhookResultMessage(inboxId) {
      try {
        const { data } = await axios.get(
          `/api/v1/accounts/${this.accountId}/smarticks/onemsg/webhook_status`,
          { params: { inbox_id: inboxId } }
        );
        if (data.webhook_status === 'registered') {
          return this.$t('INBOX_MGMT.ADD.WHATSAPP.ONEMSG.SUCCESS_WEBHOOK_OK');
        }
        if (data.webhook_status === 'failed') {
          return this.$t('INBOX_MGMT.ADD.WHATSAPP.ONEMSG.SUCCESS_WEBHOOK_FAIL');
        }
      } catch (error) {
        // fall through to the generic success message
      }
      return this.$t('INBOX_MGMT.ADD.WHATSAPP.ONEMSG.SUCCESS_MESSAGE');
    },
    async createChannel() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }

      try {
        const whatsappChannel = await this.$store.dispatch(
          'inboxes/createChannel',
          {
            name: this.inboxName.trim(),
            channel: {
              type: 'whatsapp',
              phone_number: this.phoneNumber,
              provider: 'onemsg',
              provider_config: {
                channel_id: this.channelId.trim(),
                token: this.token.trim(),
              },
            },
          }
        );

        // Surface whether 1msg accepted our callback URL (set on create),
        // instead of relying on the silent reauthorization-banner fallback.
        useAlert(await this.webhookResultMessage(whatsappChannel.id));

        router.replace({
          name: 'settings_inboxes_add_agents',
          params: {
            page: 'new',
            inbox_id: whatsappChannel.id,
          },
        });
      } catch (error) {
        useAlert(
          error.message ||
            this.$t('INBOX_MGMT.ADD.WHATSAPP.ONEMSG.ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>

<template>
  <form class="flex flex-wrap flex-col mx-0" @submit.prevent="createChannel()">
    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.inboxName.$error }">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.LABEL') }}
        <input
          v-model="inboxName"
          type="text"
          :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.PLACEHOLDER')"
          @blur="v$.inboxName.$touch"
        />
        <span v-if="v$.inboxName.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.ERROR') }}
        </span>
      </label>
    </div>

    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.phoneNumber.$error }">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.LABEL') }}
        <input
          v-model="phoneNumber"
          type="text"
          dir="ltr"
          :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.PLACEHOLDER')"
          @blur="v$.phoneNumber.$touch"
        />
        <span v-if="v$.phoneNumber.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.ERROR') }}
        </span>
      </label>
    </div>

    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.channelId.$error }">
        <span>
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.ONEMSG.CHANNEL_ID.LABEL') }}
        </span>
        <input
          v-model="channelId"
          type="text"
          dir="ltr"
          :placeholder="
            $t('INBOX_MGMT.ADD.WHATSAPP.ONEMSG.CHANNEL_ID.PLACEHOLDER')
          "
          @blur="v$.channelId.$touch"
        />
        <span v-if="v$.channelId.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.ONEMSG.CHANNEL_ID.ERROR') }}
        </span>
      </label>
    </div>

    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.token.$error }">
        <span>
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.ONEMSG.TOKEN.LABEL') }}
        </span>
        <input
          v-model="token"
          type="password"
          dir="ltr"
          autocomplete="off"
          :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.ONEMSG.TOKEN.PLACEHOLDER')"
          @blur="v$.token.$touch"
        />
        <span v-if="v$.token.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.ONEMSG.TOKEN.ERROR') }}
        </span>
      </label>
      <p class="text-n-slate-11 text-sm">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.ONEMSG.CREDENTIALS_HELP') }}
      </p>
    </div>

    <!-- Webhook URL to paste into the 1msg.io channel settings -->
    <div class="flex-shrink-0 flex-grow-0">
      <label>
        <span>
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.ONEMSG.WEBHOOK_URL.LABEL') }}
        </span>
        <div class="flex gap-2 items-center">
          <input
            :value="webhookUrl"
            type="text"
            dir="ltr"
            readonly
            class="flex-1"
            @focus="$event.target.select()"
          />
          <NextButton
            type="button"
            faded
            blue
            :disabled="!hasChannelId"
            :label="$t('INBOX_MGMT.ADD.WHATSAPP.ONEMSG.WEBHOOK_URL.COPY')"
            @click="copyWebhook"
          />
        </div>
      </label>
      <p class="text-n-slate-11 text-sm">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.ONEMSG.WEBHOOK_URL.HELP') }}
      </p>
    </div>

    <!-- Test connection (verifies credentials + the 1msg webhook) -->
    <div class="flex-shrink-0 flex-grow-0 flex gap-3 items-center mt-2">
      <NextButton
        type="button"
        faded
        blue
        :is-loading="isTesting"
        :label="$t('INBOX_MGMT.ADD.WHATSAPP.ONEMSG.TEST_BUTTON')"
        @click="testConnection"
      />
      <span v-if="testMessage" class="text-sm" :class="testClass">
        {{ testMessage }}
      </span>
    </div>

    <div class="w-full mt-4">
      <NextButton
        :is-loading="uiFlags.isCreating"
        type="submit"
        solid
        blue
        :label="$t('INBOX_MGMT.ADD.WHATSAPP.SUBMIT_BUTTON')"
      />
    </div>
  </form>
</template>
